/**
 * GenerationControls — UI para elegir número de samples antes de generar
 * Muestra pricing transparente estilo Emergent
 */
import React, { useState } from 'react';
import { Sparkles, Zap, Info } from 'lucide-react';

// Pricing constants (must match backend — Cursor model: low cost, high iteration)
const STEP_COSTS = { base: 5, voice: 4, improve: 3 };
const MULTI_SAMPLE_PRICING = { 1: 0, 2: 2, 3: 3 };
const VARIATION_COST = 3;

function CorcheaIcon({ size = 16, className = '' }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
      <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z"/>
    </svg>
  );
}

export function GenerationControls({ 
  step = 'base',
  balance = 0,
  onGenerate,
  disabled = false,
  className = ''
}) {
  const [numSamples, setNumSamples] = useState(1);
  const [showInfo, setShowInfo] = useState(false);

  const baseCost = STEP_COSTS[step] || 5;
  const extraCost = numSamples > 1 
    ? Object.entries(MULTI_SAMPLE_PRICING)
        .filter(([k]) => parseInt(k) > 1 && parseInt(k) <= numSamples)
        .reduce((sum, [, v]) => sum + v, 0)
    : 0;
  const totalCost = baseCost + extraCost;
  const canAfford = balance >= totalCost;

  const handleGenerate = () => {
    if (!canAfford || disabled) return;
    onGenerate?.({ numSamples, totalCost });
  };

  return (
    <div className={`bg-[#0F0F12] rounded-lg border border-[#27272A] p-2.5 sm:p-3 ${className}`} data-testid="generation-controls">
      {/* Sample count selector */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] sm:text-xs text-[#A1A1AA]">Variaciones</span>
        <div className="flex gap-1">
          {[1, 2, 3].map((n) => {
            const isSelected = numSamples === n;
            const thisExtraCost = n > 1 
              ? Object.entries(MULTI_SAMPLE_PRICING)
                  .filter(([k]) => parseInt(k) > 1 && parseInt(k) <= n)
                  .reduce((sum, [, v]) => sum + v, 0)
              : 0;
            
            return (
              <button
                key={n}
                onClick={() => setNumSamples(n)}
                disabled={disabled}
                data-testid={`samples-${n}`}
                className={`
                  relative px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-md text-[11px] sm:text-xs font-medium transition-all
                  ${isSelected 
                    ? 'bg-[#F5C842] text-black' 
                    : 'bg-[#1E1E23] text-[#A1A1AA] hover:bg-[#27272A] hover:text-white'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                {n}x
                {n > 1 && (
                  <span className={`absolute -top-1.5 -right-1.5 text-[8px] sm:text-[9px] px-1 rounded-full font-bold
                    ${isSelected ? 'bg-black/20 text-black' : 'bg-[#F5C842]/20 text-[#F5C842]'}
                  `}>
                    +{thisExtraCost}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Cost breakdown */}
      <div className="bg-[#0A0A0B] rounded-md p-2 sm:p-2.5 mb-2">
        <div className="flex items-center justify-between text-[10px] sm:text-xs text-[#52525B] mb-1">
          <span>Generación {step}</span>
          <span>{baseCost} corcheas</span>
        </div>
        {extraCost > 0 && (
          <div className="flex items-center justify-between text-[10px] sm:text-xs text-[#52525B] mb-1">
            <span>+{numSamples - 1} variaciones extra</span>
            <span>+{extraCost} corcheas</span>
          </div>
        )}
        <div className="border-t border-[#1E1E23] mt-1.5 pt-1.5 flex items-center justify-between">
          <span className="text-[11px] sm:text-xs font-medium text-white">Total</span>
          <div className="flex items-center gap-1">
            <CorcheaIcon size={14} className="text-[#F5C842]" />
            <span className="text-xs sm:text-sm font-bold text-[#F5C842]">{totalCost}</span>
          </div>
        </div>
      </div>

      {/* Balance indicator */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] sm:text-xs text-[#52525B]">Tu balance:</span>
        <div className="flex items-center gap-1">
          <CorcheaIcon size={12} className={canAfford ? 'text-green-400' : 'text-red-400'} />
          <span className={`text-xs sm:text-sm font-medium ${canAfford ? 'text-green-400' : 'text-red-400'}`}>
            {balance}
          </span>
        </div>
      </div>

      {/* Generate button */}
      <button
        onClick={handleGenerate}
        disabled={!canAfford || disabled}
        data-testid="generate-btn"
        className={`
          w-full py-2 sm:py-2.5 rounded-lg font-bold text-xs sm:text-sm flex items-center justify-center gap-1.5
          transition-all
          ${canAfford && !disabled
            ? 'bg-gradient-to-r from-[#F5C842] to-[#FF6B00] text-black hover:opacity-90 active:scale-[0.98]'
            : 'bg-[#27272A] text-[#52525B] cursor-not-allowed'
          }
        `}
      >
        <Sparkles size={14} />
        {disabled 
          ? 'Generando...'
          : canAfford 
            ? `Generar ${numSamples > 1 ? `${numSamples} variaciones` : ''}`
            : 'Saldo insuficiente'
        }
      </button>

      {/* Value hint */}
      {numSamples > 1 && (
        <div className="flex items-center justify-center gap-1 mt-1.5 text-[9px] sm:text-[10px] text-[#52525B]">
          <Zap size={10} className="text-[#F5C842]" />
          <span>
            {numSamples === 2 
              ? 'Ahorra 3 corcheas vs generar 2 veces separadas'
              : 'Ahorra 7 corcheas vs generar 3 veces separadas'
            }
          </span>
        </div>
      )}

      {/* Info tooltip */}
      <button
        onClick={() => setShowInfo(!showInfo)}
        className="mt-2 w-full flex items-center justify-center gap-1 text-[10px] sm:text-xs text-[#52525B] hover:text-[#A1A1AA] transition-colors"
      >
        <Info size={12} />
        <span>¿Cómo funciona?</span>
      </button>

      {showInfo && (
        <div className="mt-1.5 p-2.5 bg-[#0A0A0B] rounded-md text-[10px] sm:text-xs text-[#71717A] space-y-1">
          <p>• <strong className="text-white">1 variación</strong>: Una única generación basada en tu brief.</p>
          <p>• <strong className="text-white">2-3 variaciones</strong>: Genera múltiples versiones con ligeras diferencias creativas. Elige tu favorita sin coste adicional por escuchar.</p>
          <p>• <strong className="text-white">Variación rápida</strong> (después): Solo {VARIATION_COST} corcheas para otra versión del mismo brief.</p>
        </div>
      )}
    </div>
  );
}

export default GenerationControls;
